import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Paper, Stack, Text, TextInput, FormField } from '@vipso/shared';
import {
  useListApprovalFlowsQuery,
  useApproveEntityMutation,
} from '../api/tenantApi';
import { useCurrentMembershipId } from '../hooks/useCurrentMembershipId';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';

export type ApprovalSubjectType = 'CUSTOMER' | 'VENDOR' | 'PRODUCT' | 'WAREHOUSE' | 'PROC';

type ApprovalStep = {
  id: string;
  stepKind: string;
  status: string;
  assigneeMembershipId: string | number;
  note?: string | null;
};

type ApprovalFlow = {
  id: string;
  status: string;
  steps?: ApprovalStep[] | null;
};

type Props = {
  subjectType: ApprovalSubjectType;
  subjectId: string | undefined | null;
  /** Optional final status from the entity row (shown as badge). */
  entityApprovalStatus?: string | null;
};

/**
 * Internal approval controls on a record detail page.
 * Approve / Reject (or Acknowledge for rejection-ack) only when the logged-in
 * membership matches a pending approval_steps.assigneeMembershipId.
 */
export function EntityApprovalPanel({
  subjectType,
  subjectId,
  entityApprovalStatus,
}: Props) {
  const { t } = useTranslation();
  const membershipId = useCurrentMembershipId();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resolvedLocally, setResolvedLocally] = useState(false);

  const { data, isLoading, isFetching, refetch } = useListApprovalFlowsQuery(
    { subjectType, subjectId: String(subjectId) },
    { skip: !subjectId },
  );
  const [approveEntity, { isLoading: deciding }] = useApproveEntityMutation();

  const flows = (data?.listApprovalFlows ?? []) as ApprovalFlow[];

  const allPendingSteps = useMemo(
    () =>
      flows.flatMap((flow) =>
        (flow.steps ?? [])
          .filter((step) => String(step.status).toUpperCase() === 'PENDING')
          .map((step) => ({ flow, step })),
      ),
    [flows],
  );

  const myPendingStep = useMemo(() => {
    if (resolvedLocally || membershipId == null || membershipId === '') return null;
    return (
      allPendingSteps.find(
        ({ step }) => String(step.assigneeMembershipId) === String(membershipId),
      ) ?? null
    );
  }, [allPendingSteps, membershipId, resolvedLocally]);

  if (!subjectId) return null;
  if ((isLoading || isFetching) && !flows.length) return null;

  // Nothing to show if there is no entity status and no flows.
  if (!entityApprovalStatus && !flows.length) return null;

  const isRejectionAck = myPendingStep?.step.stepKind === 'REJECTION_ACK';
  const showActions = Boolean(myPendingStep);

  async function decide(decision: 'APPROVED' | 'REJECTED') {
    if (!subjectId || !myPendingStep) return;
    setError(null);
    try {
      await approveEntity({
        subjectType,
        subjectId: String(subjectId),
        decision,
        note: note.trim() || undefined,
      }).unwrap();
      setNote('');
      setResolvedLocally(true);
      await refetch();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message ?? '')
            : '';
      setError(message || t('approval.actionFailed'));
    }
  }

  const idleMessage = !membershipId
    ? t('approval.membershipMissing')
    : flows.length === 0
      ? t('approval.flowMissing')
      : allPendingSteps.length === 0
        ? t('approval.noPendingSteps')
        : t('approval.noActionForYou');

  return (
    <Paper p="lg" withBorder radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text fw={600}>{t('approval.internalReview')}</Text>
          <ApprovalStatusBadge
            status={entityApprovalStatus ?? myPendingStep?.flow.status ?? flows[0]?.status}
          />
        </Group>

        {showActions ? (
          <>
            <Text size="sm" c="dimmed">
              {isRejectionAck
                ? t('approval.acknowledgeRejection')
                : t('approval.yourDecision')}
            </Text>
            <FormField label={t('approval.note')}>
              <TextInput
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                placeholder={t('approval.notePlaceholder')}
              />
            </FormField>
            {error ? <Alert color="red" title={error} /> : null}
            <Group justify="flex-end">
              {!isRejectionAck ? (
                <Button
                  color="red"
                  variant="light"
                  loading={deciding}
                  onClick={() => void decide('REJECTED')}
                >
                  {t('tasks.reject')}
                </Button>
              ) : null}
              <Button loading={deciding} onClick={() => void decide('APPROVED')}>
                {isRejectionAck ? t('approval.acknowledge') : t('tasks.approve')}
              </Button>
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {idleMessage}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
